import { currentLocale, requestLocale } from "../lib/i18n";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";
// Shared fetch wrapper that injects the App Bridge session (id) token into the
// Authorization header. Tokens are short-lived (~60s) so we always fetch a
// fresh one before each request.
//
// App Bridge v4 exposes a global `window.shopify` once the CDN script tag in
// index.html has loaded. `shopify.idToken()` resolves the JWT we need.

interface ShopifyScopeQueryResult {
  granted: string[];
  required: string[];
  optional: string[];
}

interface ShopifyScopeRequestResult {
  result: string;
  detail: {
    granted: string[];
  };
}

interface ShopifyScopesApi {
  query: () => Promise<ShopifyScopeQueryResult>;
  request: (scopes: string[]) => Promise<ShopifyScopeRequestResult>;
}

interface ShopifyIntentResponse {
  code: "ok" | "closed" | "error";
  data?: Record<string, unknown>;
  message?: string;
}

interface ShopifyIntentActivity {
  complete: Promise<ShopifyIntentResponse>;
}

interface ShopifyIntentsApi {
  invoke: (
    query: string,
    options?: { value?: string },
  ) => Promise<ShopifyIntentActivity>;
}

interface ShopifyAppBridge {
  idToken: () => Promise<string>;
  config?: { shop?: string };
  scopes?: ShopifyScopesApi;
  intents?: ShopifyIntentsApi;
}

declare global {
  interface Window {
    shopify?: ShopifyAppBridge;
  }
}

/**
 * Resolve the API base URL. In production we point at the FastAPI service on
 * Fly.io; in development Vite's proxy forwards `/api` → localhost:8000.
 */
function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? "";
}

export class ShopGenerationChangedError extends Error {
  constructor() {
    super("authenticated shop changed while the request was being prepared");
    this.name = "ShopGenerationChangedError";
  }
}

interface ActiveTenantBinding {
  shopDomain: string;
  owner: object;
}

let activeTenantBinding: ActiveTenantBinding | null = null;

/** Install the verified tenant for legacy callbacks that do not carry props. */
export function bindActiveTenantShopGeneration(shopDomain: string): () => void {
  const canonical = canonicalShopDomain(shopDomain);
  if (canonical === null) throw new ShopGenerationChangedError();
  const owner = {};
  activeTenantBinding = { shopDomain: canonical, owner };
  return () => {
    if (activeTenantBinding?.owner === owner) activeTenantBinding = null;
  };
}

/** Capture the committed tenant; client URL/App Bridge signals are not enough. */
export function captureActiveTenantShopGeneration(): string {
  const active = activeTenantBinding?.shopDomain ?? null;
  if (active === null) throw new ShopGenerationChangedError();
  return active;
}

/** Accept only Shopify's canonical permanent shop domain. */
export function canonicalShopDomain(
  value: string | null | undefined,
): string | null {
  const shop = value?.trim().toLowerCase() ?? "";
  if (!shop) return null;
  return /^(?=.{1,63}\.myshopify\.com$)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.myshopify\.com$/.test(
    shop,
  )
    ? shop
    : null;
}

/** Current client-side shop generation, before `/api/me` makes it authoritative. */
export function currentClientShopDomain(search?: string): string | null {
  if (typeof window === "undefined") return null;
  const currentSearch = search ?? window.location.search;
  const queryValue = new URLSearchParams(currentSearch).get("shop");
  const appBridgeValue = window.shopify?.config?.shop;
  const devTokenValue = import.meta.env.VITE_DEV_SESSION_TOKEN ?? "";
  const fromQuery = canonicalShopDomain(queryValue);
  const fromAppBridge = canonicalShopDomain(appBridgeValue);
  const fromDevToken = tokenShopDomain(devTokenValue);
  if (
    (queryValue !== null && fromQuery === null) ||
    (appBridgeValue !== undefined && fromAppBridge === null) ||
    (devTokenValue !== "" && fromDevToken === null)
  ) {
    return null;
  }
  const available = [fromQuery, fromAppBridge, fromDevToken].filter(
    (value): value is string => value !== null,
  );
  if (available.length === 0) return null;
  return available.every((value) => value === available[0])
    ? available[0]
    : null;
}

function tokenShopDomain(token: string): string | null {
  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return null;
    const padded = payloadPart
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payloadPart.length / 4) * 4, "=");
    const payload: unknown = JSON.parse(atob(padded));
    if (
      typeof payload !== "object" ||
      payload === null ||
      Array.isArray(payload)
    ) {
      return null;
    }
    const dest = (payload as Record<string, unknown>).dest;
    if (typeof dest !== "string") return null;
    const parsed = new URL(dest);
    if (parsed.protocol !== "https:") return null;
    return canonicalShopDomain(parsed.hostname);
  } catch {
    return null;
  }
}

/** Fail before a request can cross from a stale UI generation into another shop. */
export function assertCurrentShopGeneration(expectedShopDomain: string): void {
  const expected = canonicalShopDomain(expectedShopDomain);
  const current = currentClientShopDomain();
  if (!expected || current !== expected) {
    throw new ShopGenerationChangedError();
  }
}

/** Read-only operations also discard a response if the shop changed in flight. */
export async function runInShopGeneration<T>(
  expectedShopDomain: string,
  operation: () => Promise<T>,
): Promise<T> {
  assertCurrentShopGeneration(expectedShopDomain);
  const result = await operation();
  assertCurrentShopGeneration(expectedShopDomain);
  return result;
}

async function authHeader(
  expectedShopDomain: string,
): Promise<Record<string, string>> {
  const expected = canonicalShopDomain(expectedShopDomain);
  if (expected === null) {
    throw new ShopGenerationChangedError();
  }
  assertCurrentShopGeneration(expected);
  if (typeof window !== "undefined" && window.shopify?.idToken) {
    const token = await window.shopify.idToken();
    assertCurrentShopGeneration(expected);
    const tokenShop = tokenShopDomain(token);
    if (tokenShop !== expected) {
      throw new ShopGenerationChangedError();
    }
    return { Authorization: `Bearer ${token}` };
  }
  // In tests / dev without App Bridge, fall back to a dev token from env if set
  const fallback = import.meta.env.VITE_DEV_SESSION_TOKEN;
  if (fallback) {
    assertCurrentShopGeneration(expected);
    const tokenShop = tokenShopDomain(fallback);
    if (tokenShop !== expected) {
      throw new ShopGenerationChangedError();
    }
  }
  if (!fallback) throw new ShopGenerationChangedError();
  return { Authorization: `Bearer ${fallback}` };
}

/** Shared by bounded streaming clients; never returns a cross-shop token. */
export function shopBoundAuthHeader(
  expectedShopDomain: string,
): Promise<Record<string, string>> {
  return authHeader(expectedShopDomain);
}

/**
 * エラー応答本文から画面に出せる文字列を取り出す。
 *
 * FastAPI の RequestValidationError（422）は `detail` を**配列**で返すため、
 * 素直に `parsed.detail` を代入するとテンプレートリテラルで "[object Object]" に
 * 化け、backend が用意した日本語の対処案内が一度も表示されない。配列は各要素の
 * `msg`（pydantic が付ける先頭の "Value error, " は落とす）を連結し、オブジェクトは
 * 生の本文へフォールバックする（[object Object] にはしない）。
 */
interface NormalizedErrorPayload {
  detail: string;
  code: string | null;
  deliveryId: StockyPublicBigIntId | null;
  confirmationToken: string | null;
}

function normalizeErrorPayload(text: string): NormalizedErrorPayload {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return {
      detail: text,
      code: null,
      deliveryId: null,
      confirmationToken: null,
    };
  }
  const detail = (parsed as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") {
    return { detail, code: null, deliveryId: null, confirmationToken: null };
  }
  if (detail !== null && typeof detail === "object" && !Array.isArray(detail)) {
    const structured = detail as {
      message?: unknown;
      code?: unknown;
      delivery_id?: unknown;
      confirmation_token?: unknown;
    };
    if (typeof structured.message === "string") {
      return {
        detail: structured.message,
        code: typeof structured.code === "string" ? structured.code : null,
        deliveryId: isStockyPublicBigIntId(structured.delivery_id)
          ? canonicalStockyPublicBigIntId(structured.delivery_id)
          : null,
        confirmationToken:
          typeof structured.confirmation_token === "string"
            ? structured.confirmation_token
            : null,
      };
    }
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (item as { msg?: unknown })?.msg)
      .filter((msg): msg is string => typeof msg === "string")
      .map((msg) => msg.replace(/^Value error,\s*/, ""));
    if (messages.length > 0) {
      return {
        detail: messages.join(" / "),
        code: null,
        deliveryId: null,
        confirmationToken: null,
      };
    }
  }
  return {
    detail: text,
    code: null,
    deliveryId: null,
    confirmationToken: null,
  };
}

export function normalizeErrorDetail(text: string): string {
  return normalizeErrorPayload(text).detail;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public code: string | null = null,
    public deliveryId: StockyPublicBigIntId | null = null,
    public confirmationToken: string | null = null,
  ) {
    super(`API ${status}: ${detail}`);
  }
}

// Shopify 管理画面（再インストール）や App Store からのインストールは App URL を
// 開くだけで backend の OAuth（/auth/callback でのトークン交換・uninstalled_at 解除）を
// 通らない。backend が 410（アンインストール済み）/「shop not installed」404 を返したら、
// ここでトップレベル遷移して OAuth を開始する＝インストール導線の自己修復。
const OAUTH_REDIRECT_GUARD_KEY = "ub-oauth-redirect-at";
const OAUTH_REDIRECT_GUARD_MS = 30_000;

function needsOAuthInstall(status: number, detail: string): boolean {
  if (status === 410) return true;
  return status === 404 && detail.startsWith("shop not installed");
}

/** OAuth 開始へトップレベル遷移する。ループ防止に30秒間は再遷移しない。 */
function redirectToOAuthInstall(): void {
  const shop = currentClientShopDomain();
  if (!shop) return;
  const guardKey = `${OAUTH_REDIRECT_GUARD_KEY}:${shop}`;
  const last = Number(sessionStorage.getItem(guardKey) ?? "0");
  if (Date.now() - last < OAUTH_REDIRECT_GUARD_MS) return;
  sessionStorage.setItem(guardKey, String(Date.now()));
  window.open(
    `${apiBaseUrl()}/auth/install?shop=${encodeURIComponent(shop)}&locale=${encodeURIComponent(currentLocale())}`,
    "_top",
  );
}

/** Apply the same install self-heal to JSON and bounded streaming clients. */
export function selfHealOAuthInstall(status: number, detail: string): void {
  if (needsOAuthInstall(status, detail)) {
    redirectToOAuthInstall();
  }
}

export interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Multipart uploads. The browser supplies the boundary Content-Type. */
  formData?: FormData;
  query?: Record<string, string | number | undefined>;
  /** 呼び出し固有の追加ヘッダー（認証・locale は共通層が常に上書きする）。 */
  headers?: Record<string, string>;
  /** Browser cache mode for reads that authorize a following mutation. */
  cache?: RequestCache;
  /** A stale workflow must never send its body under another shop's token. */
  expectedShopDomain?: string;
}

export function apiFetch<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  let expectedShopDomain: string;
  try {
    expectedShopDomain = captureActiveTenantShopGeneration();
  } catch (error) {
    return Promise.reject(error);
  }
  if (
    opts.expectedShopDomain !== undefined &&
    canonicalShopDomain(opts.expectedShopDomain) !== expectedShopDomain
  ) {
    return Promise.reject(new ShopGenerationChangedError());
  }
  return apiFetchForGeneration<T>(path, opts, expectedShopDomain);
}

/** The sole pre-tenant request: verify `/api/me` for the requested shop. */
export function apiFetchForShopBootstrap<T>(
  expectedShopDomain: string,
): Promise<T> {
  const expected = canonicalShopDomain(expectedShopDomain);
  if (expected === null) {
    return Promise.reject(new ShopGenerationChangedError());
  }
  assertCurrentShopGeneration(expected);
  return apiFetchForGeneration<T>("/api/me", {}, expected);
}

async function apiFetchForGeneration<T>(
  path: string,
  opts: ApiOptions,
  expectedShopDomain: string,
): Promise<T> {
  assertCurrentShopGeneration(expectedShopDomain);
  const headers: Record<string, string> = {
    ...opts.headers,
    Accept: "application/json",
    // サーバーが返すマーチャント向けエラーを画面と同じ言語で出すために送る。
    // 独自ヘッダを正本にし、汎用HTTPクライアントとの互換用にAccept-Languageも送る。
    "X-UreyukiBox-Locale": requestLocale(),
    "Accept-Language": requestLocale(),
    ...(await authHeader(expectedShopDomain)),
  };

  let url = apiBaseUrl() + path;
  if (opts.query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null) params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += (url.includes("?") ? "&" : "?") + qs;
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
    ...(opts.cache === undefined ? {} : { cache: opts.cache }),
  };
  if (opts.body !== undefined && opts.formData !== undefined) {
    throw new Error("apiFetch accepts either body or formData, not both");
  }
  if (opts.formData !== undefined) {
    init.body = opts.formData;
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }

  const response = await fetch(url, init);
  const text = await response.text();
  assertCurrentShopGeneration(expectedShopDomain);
  if (!response.ok) {
    const error = normalizeErrorPayload(text);
    selfHealOAuthInstall(response.status, error.detail);
    throw new ApiError(
      response.status,
      error.detail,
      error.code,
      error.deliveryId,
      error.confirmationToken,
    );
  }
  if (!text) return undefined as unknown as T;
  return JSON.parse(text) as T;
}
